import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Helmet } from "react-helmet-async";
import { LEGAL_LAST_UPDATED } from "@/lib/legal";
import { CONTACT_EMAIL } from "@shared/contact";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function CookiePolicyPage() {
  return (
    <>
      <Helmet>
        <title>Cookie Policy | TaxYourSalary.com</title>
        <meta name="description" content="Cookie Policy for TaxYourSalary.com. Learn about the cookies we use, their purposes, and how to manage your preferences." />
        <link rel="canonical" href="https://taxyoursalary.com/cookie-policy" />
        <link rel="icon" type="image/png" sizes="48x48" href="/favicon-48.png" />
        <link rel="icon" type="image/png" sizes="192x192" href="/favicon-192.png" />
        <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
        <meta name="msapplication-TileImage" content="/favicon-192.png" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://taxyoursalary.com/cookie-policy" />
        <meta property="og:title" content="Cookie Policy | TaxYourSalary.com" />
        <meta property="og:description" content="Cookie Policy for TaxYourSalary.com. Learn about the cookies we use, their purposes, and how to manage your preferences." />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        
        <main className="flex-1 container mx-auto px-4 py-8 max-w-4xl">
          <Card className="overflow-hidden">
            <CardContent className="p-6 md:p-8">
              <h1 className="text-3xl font-bold mb-6" data-testid="heading-cookie-policy">Cookie Policy</h1>
              <p className="text-muted-foreground mb-6">Last Updated: {LEGAL_LAST_UPDATED}</p>
              
              <div className="prose prose-sm max-w-none dark:prose-invert space-y-6">
                <section>
                  <h2 className="text-xl font-semibold mb-3">1. What Are Cookies?</h2>
                  <p className="text-foreground/80 leading-relaxed">
                    Cookies are small text files that are placed on your computer or mobile device when you visit a website. They are widely used to make websites work more efficiently and provide information to website owners. Cookies can be "persistent" (remaining on your device for a set period) or "session" (deleted when you close your browser).
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold mb-3">2. How We Use Cookies</h2>
                  <p className="text-foreground/80 leading-relaxed">
                    TaxYourSalary.com uses cookies and similar technologies for several purposes:
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-1 text-foreground/80">
                    <li><strong>To make our Site function properly</strong> (essential cookies)</li>
                    <li><strong>To analyze Site usage</strong> and improve our services (analytics cookies)</li>
                    <li><strong>To serve relevant advertisements</strong> (advertising cookies)</li>
                    <li><strong>To remember your preferences</strong> (preference cookies)</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold mb-3">3. Types of Cookies We Use</h2>
                  
                  <h3 className="text-lg font-medium mt-4 mb-2">3.1 Essential Cookies (Strictly Necessary)</h3>
                  <p className="text-foreground/80 leading-relaxed">
                    These cookies are necessary for the Site to function properly. They cannot be switched off. They are usually set in response to actions you take, such as setting privacy preferences or using features.
                  </p>
                  
                  <div className="overflow-x-auto mt-3">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Cookie Name</TableHead>
                          <TableHead>Purpose</TableHead>
                          <TableHead>Duration</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow>
                          <TableCell className="font-mono text-sm">cookie_consent</TableCell>
                          <TableCell>Stores your cookie consent preferences</TableCell>
                          <TableCell>1 year</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-mono text-sm">theme_preference</TableCell>
                          <TableCell>Remembers your dark/light mode preference</TableCell>
                          <TableCell>1 year</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>

                  <h3 className="text-lg font-medium mt-6 mb-2">3.2 Analytics Cookies</h3>
                  <p className="text-foreground/80 leading-relaxed">
                    These cookies help us understand how visitors interact with our Site by collecting and reporting information anonymously. This helps us improve our Site.
                  </p>
                  
                  <div className="overflow-x-auto mt-3">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Cookie Name</TableHead>
                          <TableHead>Provider</TableHead>
                          <TableHead>Purpose</TableHead>
                          <TableHead>Duration</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow>
                          <TableCell className="font-mono text-sm">_ga</TableCell>
                          <TableCell>Google Analytics</TableCell>
                          <TableCell>Distinguishes unique users</TableCell>
                          <TableCell>2 years</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-mono text-sm">_ga_*</TableCell>
                          <TableCell>Google Analytics</TableCell>
                          <TableCell>Stores session state</TableCell>
                          <TableCell>2 years</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-mono text-sm">_gid</TableCell>
                          <TableCell>Google Analytics</TableCell>
                          <TableCell>Distinguishes users</TableCell>
                          <TableCell>24 hours</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>

                  <h3 className="text-lg font-medium mt-6 mb-2">3.3 Advertising Cookies</h3>
                  <p className="text-foreground/80 leading-relaxed">
                    These cookies are used by advertising partners (including Google AdSense) to deliver relevant ads and track ad campaign performance. They may be used to build a profile of your interests.
                  </p>
                  <p className="text-foreground/80 leading-relaxed mt-2">
                    Before you make a choice, none of the cookies below are set. The site runs Google Consent Mode v2, which starts every advertising and analytics signal as <em>denied</em> and redacts ad click identifiers, so the ad code can load without storing anything on your device and without serving a personalized ad. They are only written once you press <strong>Accept all</strong>. The single exception is <span className="font-mono text-sm">test_cookie</span>, which Google's ad code sets on <span className="font-mono text-sm">doubleclick.net</span> purely to check whether your browser accepts cookies at all; it holds no identifier and is treated as strictly necessary.
                  </p>
                  
                  <div className="overflow-x-auto mt-3">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Cookie Name</TableHead>
                          <TableHead>Provider</TableHead>
                          <TableHead>Purpose</TableHead>
                          <TableHead>Duration</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow>
                          <TableCell className="font-mono text-sm">test_cookie</TableCell>
                          <TableCell>Google DoubleClick</TableCell>
                          <TableCell>Checks whether your browser supports cookies. No identifier, strictly necessary</TableCell>
                          <TableCell>15 minutes</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-mono text-sm">__gads</TableCell>
                          <TableCell>Google AdSense</TableCell>
                          <TableCell>Serves personalized ads</TableCell>
                          <TableCell>13 months</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-mono text-sm">__gpi</TableCell>
                          <TableCell>Google AdSense</TableCell>
                          <TableCell>Publisher identifier</TableCell>
                          <TableCell>13 months</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-mono text-sm">DSID</TableCell>
                          <TableCell>Google DoubleClick</TableCell>
                          <TableCell>Ad serving and targeting</TableCell>
                          <TableCell>2 weeks</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-mono text-sm">IDE</TableCell>
                          <TableCell>Google DoubleClick</TableCell>
                          <TableCell>Ad serving and retargeting</TableCell>
                          <TableCell>13 months</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </section>

                <section>
                  <h2 className="text-xl font-semibold mb-3">4. Third-Party Cookies</h2>
                  <p className="text-foreground/80 leading-relaxed">
                    Some cookies are set by third parties on our Site. We have no control over these cookies. The main third parties that may set cookies on our Site include:
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-2 text-foreground/80">
                    <li>
                      <strong>Google Analytics:</strong> We use Google Analytics to understand how visitors interact with our Site.
                      <a href="https://policies.google.com/technologies/cookies" className="text-primary underline ml-1" target="_blank" rel="noopener noreferrer">Learn more about Google Analytics cookies</a>
                    </li>
                    <li>
                      <strong>Google AdSense:</strong> Google AdSense displays advertisements and may use cookies for ad personalization.
                      <a href="https://policies.google.com/technologies/ads" className="text-primary underline ml-1" target="_blank" rel="noopener noreferrer">Learn more about Google advertising cookies</a>
                    </li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold mb-3">5. Your Cookie Choices</h2>
                  
                  <h3 className="text-lg font-medium mt-4 mb-2">5.1 Cookie Consent Banner</h3>
                  <p className="text-foreground/80 leading-relaxed">
                    When you first visit our Site, you will see a cookie consent banner. You can choose to:
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-1 text-foreground/80">
                    <li><strong>Accept All:</strong> Allow all cookies including analytics and advertising</li>
                    <li><strong>Reject All:</strong> Only allow essential cookies required for the Site to function</li>
                    <li><strong>Manage Preferences:</strong> Choose which categories of cookies to allow</li>
                  </ul>
                  <p className="text-foreground/80 leading-relaxed mt-3">
                    You can change your preferences at any time by clicking "Cookie Settings" in our footer.
                  </p>

                  <h3 className="text-lg font-medium mt-4 mb-2">5.2 Browser Settings</h3>
                  <p className="text-foreground/80 leading-relaxed">
                    Most web browsers allow you to control cookies through their settings. You can:
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-1 text-foreground/80">
                    <li>View what cookies are stored and delete them individually</li>
                    <li>Block third-party cookies</li>
                    <li>Block cookies from specific sites</li>
                    <li>Block all cookies</li>
                    <li>Delete all cookies when you close your browser</li>
                  </ul>
                  <p className="text-foreground/80 leading-relaxed mt-3">
                    Note: Blocking all cookies may affect your experience on many websites, not just ours.
                  </p>

                  <h3 className="text-lg font-medium mt-4 mb-2">5.3 Opt-Out Links</h3>
                  <ul className="list-disc pl-6 mt-2 space-y-2 text-foreground/80">
                    <li>
                      <strong>Google Analytics:</strong>
                      <a href="https://tools.google.com/dlpage/gaoptout" className="text-primary underline ml-1" target="_blank" rel="noopener noreferrer">Google Analytics Opt-out Browser Add-on</a>
                    </li>
                    <li>
                      <strong>Google Ads Personalization:</strong>
                      <a href="https://www.google.com/settings/ads" className="text-primary underline ml-1" target="_blank" rel="noopener noreferrer">Google Ads Settings</a>
                    </li>
                    <li>
                      <strong>Network Advertising Initiative:</strong>
                      <a href="https://www.networkadvertising.org/choices/" className="text-primary underline ml-1" target="_blank" rel="noopener noreferrer">NAI Opt-out Page</a>
                    </li>
                    <li>
                      <strong>Digital Advertising Alliance:</strong>
                      <a href="https://www.aboutads.info/choices/" className="text-primary underline ml-1" target="_blank" rel="noopener noreferrer">DAA Opt-out Page</a>
                    </li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold mb-3">6. Updates to This Policy</h2>
                  <p className="text-foreground/80 leading-relaxed">
                    We may update this Cookie Policy from time to time to reflect changes in our practices or for operational, legal, or regulatory reasons. We will notify you of any material changes by posting the updated policy on this page.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold mb-3">7. Contact Us</h2>
                  <p className="text-foreground/80 leading-relaxed">
                    If you have questions about our use of cookies, please contact us at:
                  </p>
                  <p className="text-foreground/80 mt-3">
                    <strong>Email:</strong> {CONTACT_EMAIL}<br />
                    <strong>Website:</strong> taxyoursalary.com/contact
                  </p>
                </section>
              </div>
            </CardContent>
          </Card>
        </main>
        
        <Footer />
      </div>
    </>
  );
}
